<script lang="ts">
  /**
   * 実験（lesson-format.md §7.2）: 予想を書く → 実行して比べる → 書き換えて試す。
   * 予想（出力か、コンパイルエラー / 実行時エラー）を入れるまで実行ボタンは押せない。
   * sandboxd で実行できないときは、Swift 6.2 で記録した結果と比べる。正誤は Leitner に入れない。
   */
  import { onDestroy } from 'svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import OutputLines from '../OutputLines.svelte';
  import { plainLines } from '../output-lines';
  import { SUBMIT_CHORD_LABEL, isSubmitChord } from '../keyboard';
  import { FIXED_CHOICES } from '../cards/predict-output';
  import { RunState } from '../cards/run-state.svelte';
  import RunStatus from '../cards/RunStatus.svelte';
  import RunBlock from './RunBlock.svelte';
  import RunResult from './RunResult.svelte';
  import {
    canRun,
    differsFromRecord,
    observedFromExpect,
    observedFromRun,
    outcomeLabel,
    predictionMatches,
    type Observed,
    type Prediction,
  } from './experiment';
  import { runLessonCode, type RunOutcomeView } from './run-lesson';
  import type { ExperimentBlock } from './types';

  interface Props {
    block: ExperimentBlock;
    /** 予想を出して比べたとき（レッスンの進捗に残す。§8.2） */
    oncompared?: ((id: string, matched: boolean) => void) | undefined;
  }

  let { block, oncompared }: Props = $props();

  const uid = $props.id();
  let choice = $state<'output' | 'compile-error' | 'runtime-error'>('output');
  let text = $state('');
  const run = new RunState();
  onDestroy(() => run.dispose());

  /** 比べた結果。source = run は実際に実行、record は記録した結果 */
  let compared = $state<{
    prediction: Prediction;
    observed: Observed;
    source: 'run' | 'record';
    run?: RunOutcomeView;
  } | null>(null);
  let tryAgain = $state(false);

  const prediction = $derived<Prediction>(
    choice === 'output' ? { kind: 'output', text } : { kind: choice },
  );
  const ready = $derived(canRun(prediction));
  const matched = $derived(
    compared ? predictionMatches(compared.prediction, compared.observed) : false,
  );
  const recordDiffers = $derived(
    compared?.source === 'run' ? differsFromRecord(block.expect, compared.observed) : false,
  );

  async function execute() {
    if (!ready || run.running || compared) return;
    const p = prediction;
    const res = await run.start((hooks) => runLessonCode(block.code, block.stdin ?? '', hooks));
    if (res) {
      finish({ prediction: p, observed: observedFromRun(res), source: 'run', run: res });
    }
  }

  /** sandboxd が使えないとき: 記録した結果と比べる */
  function compareWithRecord() {
    if (!ready || compared) return;
    finish({ prediction, observed: observedFromExpect(block.expect), source: 'record' });
  }

  function finish(c: NonNullable<typeof compared>) {
    compared = c;
    oncompared?.(block.id, predictionMatches(c.prediction, c.observed));
  }

  function onPredictKeydown(e: KeyboardEvent) {
    if (isSubmitChord(e)) {
      e.preventDefault();
      void execute();
    }
  }
</script>

<div class="experiment" data-testid="experiment" data-experiment-id={block.id}>
  <CodeBlock code={block.code.replace(/\n$/, '')} label="実験 {block.index} のコード" />

  {#if block.stdin !== undefined}
    <div>
      <h4 class="sub">標準入力</h4>
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <pre class="output" tabindex="0" aria-label="標準入力">{block.stdin.replace(/\n$/, '') ||
          '（なし）'}</pre>
    </div>
  {/if}

  <fieldset class="predict" disabled={compared !== null} data-testid="prediction">
    <legend>予想</legend>
    <label class="choice" class:selected={choice === 'output'}>
      <input type="radio" name="{uid}-p" value="output" bind:group={choice} />
      <span>出力を書く</span>
    </label>
    <textarea
      class="predict-input"
      rows="3"
      spellcheck="false"
      autocapitalize="off"
      autocomplete="off"
      aria-label="予想する出力"
      placeholder="1 行目&#10;2 行目"
      data-testid="prediction-input"
      bind:value={text}
      onfocus={() => (choice = 'output')}
      oninput={() => (choice = 'output')}
      onkeydown={onPredictKeydown}></textarea>
    {#each FIXED_CHOICES as c (c.kind)}
      <label class="choice" class:selected={choice === c.kind}>
        <input type="radio" name="{uid}-p" value={c.kind} bind:group={choice} />
        <span>{c.label}</span>
      </label>
    {/each}
  </fieldset>

  {#if !compared}
    <div class="actions">
      <button
        class="btn btn-primary"
        type="button"
        data-testid="experiment-run"
        disabled={!ready || run.running}
        aria-busy={run.running ? 'true' : undefined}
        aria-describedby={ready ? undefined : `${uid}-why`}
        onclick={() => void execute()}
        >{run.running ? '実行中' : '実行して比べる'}<kbd>{SUBMIT_CHORD_LABEL}</kbd></button
      >
      {#if !ready}
        <span id="{uid}-why" class="note" data-testid="experiment-needs-prediction"
          >予想を書くか、エラーを選ぶと実行できます。</span
        >
      {/if}
    </div>
    <RunStatus {run} busyLabel="実行中" onretry={() => void execute()} />
    {#if run.error && !run.running}
      <div class="actions">
        <button
          class="btn btn-sm"
          type="button"
          data-testid="experiment-record"
          onclick={compareWithRecord}>Swift 6.2 で記録した結果と比べる</button
        >
      </div>
    {/if}
  {/if}

  <div role="status" aria-live="polite" class="verdict-region">
    {#if compared}
      <p class="verdict" class:match={matched} data-testid="experiment-verdict">
        <span class="mark" aria-hidden="true">{matched ? '✓' : '✕'}</span>
        {matched ? '予想どおりでした。' : '予想と違いました。'}
      </p>
    {/if}
  </div>

  {#if compared}
    <div class="compare" data-testid="experiment-compare">
      <div class="col">
        <h4 class="sub">予想</h4>
        {#if compared.prediction.kind === 'output'}
          <OutputLines lines={plainLines(compared.prediction.text)} label="予想" />
        {:else}
          <p class="outcome">{outcomeLabel(compared.prediction.kind)}</p>
        {/if}
      </div>
      <div class="col">
        <h4 class="sub">{compared.source === 'run' ? '実際の結果' : 'Swift 6.2 で記録した結果'}</h4>
        {#if compared.observed.kind === 'ok'}
          <OutputLines
            lines={plainLines(compared.observed.stdout)}
            label={compared.source === 'run' ? '実際の出力' : '記録した出力'}
          />
        {:else}
          <p class="outcome">{outcomeLabel(compared.observed.kind)}</p>
        {/if}
      </div>
    </div>

    {#if compared.run && compared.run.kind !== 'ok'}
      <details class="detail">
        <summary>実行の詳細</summary>
        <RunResult result={compared.run} />
      </details>
    {/if}

    {#if recordDiffers}
      <div class="recorded" data-testid="experiment-record-differs">
        <h4 class="sub">Swift 6.2 で記録した結果</h4>
        {#if block.expect.outcome === 'ok'}
          <OutputLines lines={plainLines(block.expect.expected)} label="記録した出力" />
        {:else}
          <p class="outcome">{outcomeLabel(block.expect.outcome)}</p>
        {/if}
        <p class="note">実際の結果が、記録した結果と異なります。</p>
      </div>
    {/if}

    {#if tryAgain}
      <RunBlock
        code={block.code}
        stdin={block.stdin}
        expect={block.expect}
        label="実験 {block.index} を書き換えて試す"
        editing
      />
    {:else}
      <div class="actions">
        <button
          class="btn btn-sm"
          type="button"
          data-testid="experiment-edit"
          onclick={() => (tryAgain = true)}>書き換えて試す</button
        >
      </div>
    {/if}
  {/if}
</div>

<style>
  .experiment {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    min-width: 0;
  }
  .sub {
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .predict {
    border: 0;
    padding: 0;
    margin: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .predict legend {
    font-size: var(--fs-sm);
    font-weight: var(--fw-medium);
    margin-bottom: var(--space-2);
    padding: 0;
  }
  .predict-input {
    font-family: var(--font-mono);
    font-size: var(--fs-code);
  }
  .choice {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: var(--tap-min);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    background: var(--surface);
    font-weight: var(--fw-regular);
    cursor: pointer;
  }
  .choice.selected {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .choice:has(input:focus-visible) {
    outline: var(--focus-width) solid var(--focus);
    outline-offset: var(--focus-offset);
  }
  .choice input:focus-visible {
    outline: none;
  }
  fieldset:disabled .choice {
    cursor: default;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .actions kbd {
    margin-left: var(--space-2);
  }
  .note {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .verdict {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-weight: var(--fw-semibold);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    background: var(--warn-bg);
    color: var(--warn-text);
  }
  .verdict.match {
    background: var(--ok-bg);
    color: var(--ok-text);
  }
  .mark {
    font-weight: var(--fw-bold);
  }
  .compare {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--space-3);
  }
  .col {
    min-width: 0;
  }
  .outcome {
    font-weight: var(--fw-semibold);
  }
  .detail summary {
    cursor: pointer;
    color: var(--link);
    font-size: var(--fs-sm);
  }
  .recorded {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  @media (max-width: 639px) {
    .compare {
      grid-template-columns: minmax(0, 1fr);
    }
  }
</style>
